import { useCallback, useEffect, useRef, useState } from 'react';
import { terminal } from '@/lib/trackizy';

export function useTerminalHistory({ deviceIds, orgId, types, from, to, limit = 200, live = true, pollMs = 5000 } = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const seenIds = useRef(new Set());

  const load = useCallback(async (opts = {}) => {
    setError(null);
    if (opts.reset) { setLoading(true); seenIds.current = new Set(); setRows([]); }
    try {
      const params = { limit };
      if (deviceIds?.length) params.deviceIds = deviceIds.join(',');
      if (orgId) params.orgId = orgId;
      if (types?.length) params.types = types.join(',');
      if (opts.from || from) params.from = opts.from || from;
      if (opts.to || to) params.to = opts.to || to;
      const { rows: newRows } = await terminal.history(params);
      setRows(prev => {
        const merged = [...prev];
        for (const r of newRows) {
          if (!seenIds.current.has(r.id)) { seenIds.current.add(r.id); merged.push(r); }
        }
        return merged.sort((a, b) => (a.ts < b.ts ? 1 : -1)).slice(0, limit * 2);
      });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceIds?.join(','), orgId, types?.join(','), from, to, limit]);

  useEffect(() => { load({ reset: true }); }, [load]);

  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => load(), pollMs);
    return () => clearInterval(id);
  }, [live, pollMs, load]);

  return { rows, loading, error, refresh: () => load({ reset: true }), loadEarlier: (from, to) => load({ from, to }) };
}
